import { ApiRequestsContext, CenteredFadeInSpinner, CenteredMessage } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Query } from 'react-apollo';

import { Messages } from '../../../gen-src/Localize';
import {
  DatasourceQuery,
  DatasourceQuery_publishedDatasources,
  DatasourceQueryVariables,
} from '../../../gen-src/queries/DatasourceQuery';
import { DatasourceQueryGql } from '../../queries/DatasourceQueryGql';
import { renderQueryResult } from '../renderQueryResult';
import { createRenderDataWithErrorFunction, renderError, renderNoData } from '../widgets/ErrorMessageWidgets';

export type DatasourceQueryWrapperWithContextProps = {
  readonly vizportalUrlId: string;
  readonly children: (result: DatasourceQuery_publishedDatasources, canSetDescription: boolean) => React.ReactNode;
};

export type DatasourceQueryWrapperProps = DatasourceQueryWrapperWithContextProps & {
  readonly canSetDescription: boolean;
};

// React Hooks can't be used inside a component class, so this function uses Hooks
// functionality to check permissions.
export function DatasourceQueryWrapperWithContext(props: DatasourceQueryWrapperWithContextProps): JSX.Element {
  const apiRequests = React.useContext(ApiRequestsContext);
  const [canSetDescription, setCanSetDescription] = React.useState(false);

  React.useEffect(
    () => {
      apiRequests.canUserSetFieldDescription({ datasourceVizportalId: props.vizportalUrlId ?? '' })
        .then(r => setCanSetDescription(r))
        .catch(() => { return; });
    },
    [apiRequests, props.vizportalUrlId]
  );

  return <DatasourceQueryWrapper {...props} canSetDescription={canSetDescription} />;
}

class DatasourceQueryWrapper extends React.Component<DatasourceQueryWrapperProps> {

  public render(): React.ReactNode {
    return (
      <Query<DatasourceQuery, DatasourceQueryVariables>
        query={DatasourceQueryGql}
        variables={{ vizportalUrlId: this.props.vizportalUrlId }}
      >
        {result => renderQueryResult(result, {
          renderLoading: () => <CenteredFadeInSpinner />,
          renderData: this.renderDatasourceData,
          renderNoData: renderNoData,
          renderDataWithError: createRenderDataWithErrorFunction(this.renderDatasourceData),
          renderError: renderError,
        })}
      </Query>
    );
  }

  private readonly renderDatasourceData = (data: DatasourceQuery): React.ReactNode => {
    const datasource = data.publishedDatasources ? data.publishedDatasources[0] : null;

    if (datasource) {
      return this.props.children(datasource, this.props.canSetDescription);
    } else {
      return <CenteredMessage message={Messages.ErrorItemNotFound()} />;
    }
  }

}
